/** @jsxRuntime automatic @jsxImportSource react */ // [C35] the PDF worker loads these files through tsx outside its own tsconfig
// Extracted from original/src/components/ProfessionalPrint.tsx:105-1594 (the `ProfessionalPrint` component),
// NON-SALE path only: the generic A4 statement / report layout used for "كشف حركات" of a box or vault and, later,
// for every statement print (ADR-08 §2.1 "copy, then cut logic"). Kept verbatim: the page frame (:884-914), the
// company header (:915-1011), the subject line and statement-date bar (:1094-1132), the salutation (:1134-1143),
// the data table with the numeric-column direction rule and the stacked IQD/USD balance cell (:1147-1305), the
// plain summary grid (:1546-1561), the footer with page numbering (:1579-1588), the column-width weights (:578-631)
// and the row-weight pagination (:633-827, statement branch).
//
// Cut (B4 scope, this file):
//   - every sales-invoice branch: `isCashSale*` / `isSaleInvoice` detection (:127-147), `salesSummaryDetails`
//     (:149-190), the sales summary box (:1014-1091), sales pagination (:682-711), buyer name / phone / address
//     (:113-115, :125, :983-994) and the buyer/cashier signature block (:1565-1577) — sale invoices get their own
//     template later;
//   - `tafqeetNumber` / `tafqeetDual` and every amount-in-words block (:29-103, :395, :519-521, :1400-1405,
//     :1538-1543) — D-148 amount in words arrives in B5 from the server;
//   - the customer-statement summary boxes `getStructuredSummary` / `getDualStatementSummary` (:230-546,
//     :1307-1545): label-matching heuristics, `parseFloat` on money and `Math.abs(after - before)` arithmetic in the
//     UI (forbidden, ADR-02 §2.9). The customer-statement template (D03) will receive those figures as props;
//   - `getDisplaySubject` / `getAccountTypeLabel` (:548-576): title keyword heuristics; the container passes the
//     final title. The header badge still shows `customerName || title` as the old code did (:971);
//   - `new Date()` for the issue date (:122, :977) → `dateLabel` prop; the min/max date scan over `row.date` with
//     `new Date(str)` (:829-851) → `periodFrom` / `periodTo` props (formatted by the container);
//   - the "remaining balance" bar's label matching + `parseFloat` (:853-879) → `remainingBalance` prop
//     (preformatted amount + currency suffix);
//   - the default `companySettings` object and `showProfitInPrints` column/summary filtering (:192-221) →
//     required `office` prop; the container decides which columns are sent;
//   - the bundled logo asset and the `<img onError>` handler (:3, :948-955): the image renders only when
//     `office.logoUrl` is set; the black fallback tile (:957-964) is kept as it was (it sat behind the image);
//   - `letterCode` (:223-228, unused), `replaceUsernamesInText` (:24-27, unused), `oklchToHsl` (:1596-1681),
//     `generatePDFBlob` / `generatePDF` (:1683-2005, html2canvas + jsPDF + Google Fonts @import) and
//     `printElement` (:2007-2235, iframe + document.write) → see ./print.ts.
// Strings: all inline `isAr ? … : …` texts became `t("print.*")` keys (StatementA4.keys.json), texts unchanged.
// Added: `data-testid="statement-a4"` on the root; `numeric?` on columns so the container can state the direction
// explicitly where the old header/key heuristic (`isNumericColumn`) would miss.
import { useMemo } from 'react';
import type { CSSProperties } from 'react';
import { createT } from '@faylasuf/i18n';
import type { Locale } from '@faylasuf/i18n';
import type { SettlementPair, StatementSettlement } from './settlement.js';

const NUMERIC_KEYS = new Set([
  'c_in', 'c_out', 'c_in_usd', 'c_out_usd', 'comm', 'runningBalance', 'runningBalanceUSD',
  'amount', 'balance', 'quantity', 'price', 'date', 'id', 'no', 'seq', 'index',
  'effectiveInUSD', 'effectiveOutUSD', 'rate', 'c_in_iqd', 'c_out_iqd', 'profit', 'c_in_iqd_str'
]);

const isNumericColumn = (key: string = '', header: string = '') => {
  const kLower = key.toLowerCase();
  const hLower = header.toLowerCase();
  if (NUMERIC_KEYS.has(key) || NUMERIC_KEYS.has(kLower)) return true;
  if (kLower.includes('amount') || kLower.includes('balance') || kLower.includes('price') || kLower.includes('qty') || kLower.includes('comm') || kLower.includes('date') || kLower.includes('rate') || kLower.includes('sum') || kLower.includes('total')) return true;
  if (hLower.includes('وارد') || hLower.includes('صادر') || hLower.includes('عمولة') || hLower.includes('رصيد') || hLower.includes('تاريخ') || hLower.includes('مبلغ') || hLower.includes('سعر') || hLower.includes('عدد') || hLower.includes('كمية') || hLower.includes('إجمالي') || hLower.includes('مجموع')) return true;
  return false;
};

/** Office identity printed in the header and footer; every string supplied by the container (old `companySettings`). */
export interface PrintOffice {
  readonly name: string;
  readonly phone1?: string;
  readonly phone2?: string;
  readonly address?: string;
  readonly managerName?: string;
  readonly accountantName?: string;
  /** Absolute or same-origin URL of the office logo; when absent the black fallback tile is shown. */
  readonly logoUrl?: string;
}

export interface PrintColumn {
  readonly header: string;
  readonly key: string;
  /** Forces LTR / numeric rendering of the column (otherwise the old header/key heuristic decides). */
  readonly numeric?: boolean;
  /** [C36] D-148: a profit or commission column — dropped (with its cells) when the office hides profit on documents. */
  readonly profit?: boolean;
}

/** One summary line; `value` is preformatted by the container (e.g. "1,250,000 د.ع"). */
export interface PrintSummaryItem {
  readonly label: string;
  readonly value: string;
  /** [C36] D-148: a profit or commission figure — dropped when the office hides profit on documents. */
  readonly profit?: boolean;
}

/** One table row: column key → preformatted cell text. Missing / empty keys print as "-". */
export type PrintRow = Readonly<Record<string, string>>;

export interface StatementA4Props {
  /** Document title (also the header badge when no `customerName`). */
  readonly title: string;
  /** Issue date, formatted by the container (old: `new Date().toLocaleDateString(...)`). */
  readonly dateLabel: string;
  /** Document number, printed as `#docNumber`. */
  readonly docNumber: string;
  readonly office: PrintOffice;
  readonly columns: readonly PrintColumn[];
  readonly rows: readonly PrintRow[];
  readonly summary?: readonly PrintSummaryItem[];
  /** Shown in the header badge instead of the title (old `finalCustomerName || title`). */
  readonly customerName?: string;
  readonly locale: Locale;
  /** Statement period, formatted by the container; both absent prints "-", equal values print one date. */
  readonly periodFrom?: string;
  readonly periodTo?: string;
  /** Remaining-balance bar (old "المبلغ المتبقي"); amount and currency suffix preformatted by the container. */
  readonly remainingBalance?: { readonly amount: string; readonly currency: string };
  /** [C35] D-148 amount in words of the closing balance, computed by the server from exact decimals (one phrase per
   *  currency with its side "لكم / عليكم"); printed under the summary box as the old structured / dual summaries did
   *  (`ProfessionalPrint.tsx:1400-1405`, `:1538-1543`). */
  readonly wordsLabel?: string | null;
  /** [C36] D09 T-13: the customer statement's dual-currency settlement box (old `getStructuredSummary`,
   *  `ProfessionalPrint.tsx:270-420`, `:1408-1545`) from structured figures (D-148); replaces `summary` when given. */
  readonly settlement?: StatementSettlement | null;
  /** [C36] D-148 / D-337: the office hides profit on documents — `profit: true` columns (with their cells), summary
   *  lines and the settlement's commission line are not printed. The API sets it on every hidden-profit PDF too. */
  readonly hideProfit?: boolean;
}

interface PageInfo {
  data: PrintRow[];
  showSalutation: boolean;
  showSummary: boolean;
}

// Calculate dynamic row height weight to accurately prevent any pagination overflow or leakage (:634-670)
// [C36] F-365 / D-342: measured with the PDF worker (template pages vs printed sheets), the old additive weights let
// rows overflow: a row is as tall as its tallest cell, and the old function never read the `description` key the
// customer statement uses. Now: weight = 1 + 0.25 × (lines of the tallest text cell − 1), lines estimated per column
// type (note ≈ 38 characters a line, description / details / category ≈ 10); a one-line row stays 1.0 as before.
const linesOf = (text: string, perLine: number): number => (text.length === 0 ? 1 : Math.ceil(text.length / perLine));
const calculateRowWeight = (row: PrintRow | undefined): number => {
  if (!row) return 1.0;
  const noteStr = String(row['note'] || row['notes'] || row['ملاحظة'] || row['ملاحظات'] || '').trim();
  const descStr = String(row['description'] || row['item'] || row['details'] || row['تفاصيل'] || row['بيان'] || '').trim();
  const catStr = String(row['category'] || row['تصنيف'] || '').trim();

  const lines = Math.max(linesOf(noteStr, 38), linesOf(descStr, 10), linesOf(catStr, 10));
  let weight = 1.0 + 0.25 * (lines - 1);

  // Dual currency stacked values (IQD + USD) in amounts/balances
  const balStr = String(row['balance'] || row['الرصيد'] || '');
  const inStr = String(row['incoming'] || row['وارد'] || row['deposit'] || '');
  const outStr = String(row['outgoing'] || row['صادر'] || row['withdraw'] || '');
  if (balStr.includes('\n') || inStr.includes('\n') || outStr.includes('\n')) {
    weight += 0.05;
  }

  return weight;
};

// Dynamic pagination to adapt table height to actual movements and prevent pagination leakage or awkward 1-row pages
// (:673-827, statement branch; the sales-invoice branch :682-711 is cut)
const getPagesInfo = (data: readonly PrintRow[], tallSummary = false): PageInfo[] => {
  if (!data || data.length === 0) {
    return [{
      data: [],
      showSalutation: true,
      showSummary: true
    }];
  }

  const rowWeights = data.map(calculateRowWeight);
  const totalWeight = rowWeights.reduce((a, b) => a + b, 0);

  // Calibrated capacity limits (in weight units) tailored for full A4 297mm height
  // - Small statements (up to 12-13 movements) fit entirely on a single page with summary
  // - Multi-page statements (20+ movements) strictly respect 2cm+ bottom margin clearance
  // [C36] D-342: the settlement box is about one row taller than the plain grid (measured: 13 one-line rows + the box
  // overflowed one sheet)
  const capP1WithSummary = tallSummary ? 12.5 : 13.5;
  const capP2WithSummary = tallSummary ? 10.5 : 11.5;
  const capP1NoSummary = 16.0;
  const capP2NoSummary = 18.0;

  // Single page case: Fits entirely on Page 1 WITH Summary (for small & medium statements)
  // [C36] F-365 (O-9): the old `|| data.length <= 12` forced one page for any twelve rows however tall; with the page
  // box 12 mm shorter since F-290, twelve long-text rows spilled onto an unframed second sheet ("صفحة 1 من 1"). The
  // weight check alone decides now; twelve ordinary rows (weight 12 ≤ 13.5) still print on one page as before.
  if (totalWeight <= capP1WithSummary) {
    return [{
      data: [...data],
      showSalutation: true,
      showSummary: true
    }];
  }

  const pagesList: PageInfo[] = [];
  let currentIndex = 0;

  while (currentIndex < data.length) {
    const isFirst = pagesList.length === 0;
    const maxCapWithSummary = isFirst ? capP1WithSummary : capP2WithSummary;
    const maxCapWithoutSummary = isFirst ? capP1NoSummary : capP2NoSummary;

    // Calculate total remaining weight from current index to end
    let remainingWeight = 0;
    for (let i = currentIndex; i < data.length; i++) {
      remainingWeight += rowWeights[i] ?? 1.0;
    }

    // If all remaining items fit in this page WITH the summary box
    if (remainingWeight <= maxCapWithSummary) {
      pagesList.push({
        data: data.slice(currentIndex),
        showSalutation: isFirst,
        showSummary: true
      });
      break;
    }

    // Count how many items can fit without summary
    let currentWeight = 0;
    let countWithoutSummary = 0;

    for (let i = currentIndex; i < data.length; i++) {
      const nextWeight = rowWeights[i] ?? 1.0;
      if (countWithoutSummary > 0 && currentWeight + nextWeight > maxCapWithoutSummary) {
        break;
      }
      currentWeight += nextWeight;
      countWithoutSummary++;
    }

    if (countWithoutSummary === 0) countWithoutSummary = 1;

    const remainingItemsAfter = data.length - (currentIndex + countWithoutSummary);
    let takeCount = countWithoutSummary;

    // Smart Look-Ahead & Balancing across pages:
    // Avoid orphan rows (1-2 items) on subsequent pages while filling current page naturally
    if (remainingItemsAfter > 0) {
      if (remainingItemsAfter < 3 && takeCount > 4) {
        takeCount = Math.max(3, takeCount - 2);
      }
    } else {
      // remainingItemsAfter is 0, but they could not fit with summary:
      // Split evenly between current page and next page with summary
      const totalCount = data.length - currentIndex;
      const targetLastCount = Math.max(3, Math.floor(totalCount / 2));

      let lastWeight = 0;
      let lastCount = 0;
      for (let i = data.length - 1; i >= currentIndex; i--) {
        const w = rowWeights[i] ?? 1.0;
        if (lastCount > 0 && lastWeight + w > capP2WithSummary) break;
        lastWeight += w;
        lastCount++;
        if (lastCount >= targetLastCount) break;
      }
      takeCount = Math.max(1, totalCount - lastCount);
    }

    pagesList.push({
      data: data.slice(currentIndex, currentIndex + takeCount),
      showSalutation: isFirst,
      showSummary: false
    });
    currentIndex += takeCount;
  }

  // Safety check: Ensure the summary is rendered on a page that can safely hold it
  const lastPage = pagesList[pagesList.length - 1];
  if (lastPage && !lastPage.showSummary) {
    let lastPageWeight = 0;
    for (const r of lastPage.data) {
      lastPageWeight += calculateRowWeight(r);
    }
    const allowedCap = (pagesList.length === 1) ? capP1WithSummary : capP2WithSummary;
    if (lastPageWeight <= allowedCap) {
      lastPage.showSummary = true;
    } else {
      // Add a dedicated clean summary page
      pagesList.push({
        data: [],
        showSalutation: false,
        showSummary: true
      });
    }
  }

  return pagesList;
};

// Dynamic proportional column widths to prevent layout distortion (sum to exactly 100%) (:579-631)
const computeColumnWidths = (columns: readonly PrintColumn[]): string[] => {
  const weights: Record<string, number> = {
    date: 8.5,
    category: 9.0,
    description: 10.0,
    details: 10.0,
    item: 10.0,
    note: 33.0,
    notes: 33.0,
    incoming: 10,
    in: 10,
    outgoing: 10,
    out: 10,
    comm: 6.5,
    commission: 6.5,
    balance: 14,
    account: 11,
    action: 9,
    by: 9,
    wallet: 11,
    operator: 9,
    profit: 10,
    amount: 11,
    from: 11,
    to: 11,
  };

  let totalWeight = 0;
  const colWeights = columns.map(col => {
    const key = (col.key || '').toLowerCase();
    const header = (col.header || '').toLowerCase();

    let w = weights[key];
    if (w === undefined) {
      if (key.includes('date') || header.includes('تاريخ')) w = 8.5;
      else if (key.includes('note') || header.includes('ملاحظ')) w = 30.0;
      else if (key.includes('desc') || key.includes('detail') || header.includes('تفاصيل') || header.includes('بيان')) w = 11.0;
      else if (key.includes('category') || header.includes('تصنيف')) w = 9.5;
      else if (key.includes('comm') || header.includes('عمول')) w = 8.0;
      else if (key.includes('in') || header.includes('وارد') || header.includes('له')) w = 10.0;
      else if (key.includes('out') || header.includes('صادر') || header.includes('عليه')) w = 10.0;
      else if (key.includes('balance') || header.includes('رصيد')) w = 13.0;
      else w = 10.0; // default weight
    }
    totalWeight += w;
    return w;
  });

  return columns.map((_col, idx) => {
    const pct = ((colWeights[idx] ?? 10.0) / totalWeight) * 100;
    return `${pct.toFixed(1)}%`;
  });
};

/** Sort key of a row date cell: ISO-like or day-first dates as YYYYMMDDHHMM, anything else as itself (old text fallback). */
const dateKey = (v: string): { key: string; parsed: boolean } => {
  const iso = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(v);
  if (iso) return { key: `${iso[1]}${iso[2]}${iso[3]}${iso[4] ?? '00'}${iso[5] ?? '00'}`, parsed: true };
  const dmy = /^(\d{2})-(\d{2})-(\d{4})(?:[ T](\d{2}):(\d{2}))?/.exec(v);
  if (dmy) return { key: `${dmy[3]}${dmy[2]}${dmy[1]}${dmy[4] ?? '00'}${dmy[5] ?? '00'}`, parsed: true };
  return { key: v, parsed: false };
};

function rowDateRange(rows: readonly PrintRow[]): { min: string; max: string } | null {
  const cells = rows.map((r) => (r as Record<string, unknown>)['date']).filter((d): d is string | number => d !== undefined && d !== null && String(d).trim() !== '').map((d) => String(d).trim());
  if (cells.length === 0) return null;
  const keyed = cells.map((c) => ({ c, ...dateKey(c) }));
  const pool = keyed.some((k) => k.parsed) ? keyed.filter((k) => k.parsed) : keyed;
  const sorted = [...pool].sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  return { min: sorted[0]!.c, max: sorted[sorted.length - 1]!.c };
}

export function StatementA4(props: StatementA4Props) {
  // [C36] D-337: one hide-profit rule for the browser print and the server PDF
  const p: StatementA4Props = props.hideProfit
    ? { ...props, columns: props.columns.filter((c) => !c.profit), ...(props.summary ? { summary: props.summary.filter((s) => !s.profit) } : {}), ...(props.settlement ? { settlement: { ...props.settlement, commission: null } } : {}) }
    : props;
  const { title, dateLabel, docNumber, office, columns, rows, summary, customerName, locale, periodFrom, periodTo, remainingBalance } = p;
  const t = useMemo(() => createT(locale), [locale]);
  const isAr = locale === 'ar';
  const settings = office;
  const displayDocNumber = docNumber;
  const finalCustomerName = customerName || '';

  const columnWidths = useMemo(() => computeColumnWidths(columns), [columns]);

  // [C29] F-288: without a period from the container, the range comes from the rows' `date` cells as the old template
  // did (ProfessionalPrint.tsx:829-851: earliest and latest row date, printed as written); the old `new Date(str)`
  // parse is replaced by a fixed one for the two formats the containers write (YYYY-MM-DD[ HH:MM], DD-MM-YYYY), other
  // strings sorting as text like the old fallback.
  const derived = periodFrom === undefined && periodTo === undefined ? rowDateRange(rows) : null;
  const minDate = periodFrom ?? derived?.min ?? '';
  const maxDate = periodTo ?? derived?.max ?? '';

  const pagesInfo = getPagesInfo(rows, !!p.settlement);

  return (
    <div id="professional-print-template" data-testid="statement-a4" className="p-0 sm:p-8 min-h-screen bg-gray-100 print:p-0" style={{ backgroundColor: '#f3f4f6' }}>
      {pagesInfo.map((pageInfo, pageIndex) => {
        const isLastPage = pageIndex === pagesInfo.length - 1;
        return (
          <div
            key={pageIndex}
            className="professional-page text-black relative mx-auto mb-8 shadow-none print:shadow-none print:mb-0 flex flex-col justify-start"
            style={{
              width: '210mm',
              height: '297mm',
              maxHeight: '297mm',
              minHeight: '297mm',
              padding: '10mm 12mm 24mm 12mm',
              backgroundColor: '#ffffff',
              color: '#000000',
              fontFamily: 'Cairo, system-ui, -apple-system, sans-serif',
              direction: isAr ? 'rtl' : 'ltr',
              pageBreakAfter: isLastPage ? 'auto' : 'always',
              pageBreakInside: 'avoid',
              breakAfter: isLastPage ? 'auto' : 'page',
              breakInside: 'avoid',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              position: 'relative',
              overflow: 'hidden',
              border: 'none',
              boxShadow: 'none'
            }}
          >
            {/* Header Section - Rich company & settings identity details */}
            <div className="relative z-10 mb-2 overflow-hidden flex-shrink-0">
              <div className="p-3 sm:p-3.5 rounded-2xl flex justify-between items-center bg-slate-50 border border-slate-300 shadow-xs" style={{ borderColor: '#334155', borderWidth: '1.5px' }}>

                {/* Right Side: Company details */}
                <div className="space-y-1 w-[38%] max-w-[38%]">
                  <h1 className="text-lg sm:text-xl font-black" style={{ color: '#000000', lineHeight: '1.2', letterSpacing: 'normal' }}>
                    {settings.name}
                  </h1>

                  {/* Highly customizable physical details */}
                  <div className="text-[10px] font-bold text-gray-700 space-y-0.5" style={{ lineHeight: '1.3', letterSpacing: 'normal' }}>
                    {settings.address && (
                      <div className="flex items-center gap-1">
                        <span className="text-black font-black">{t('print.address_label')}</span>
                        <span>{settings.address}</span>
                      </div>
                    )}
                    {(settings.phone1 || settings.phone2) && (
                      <div className="flex items-center gap-1">
                        <span className="text-black font-black">{t('print.phone_label')}</span>
                        <span className="font-mono text-gray-800">
                          {settings.phone1} {settings.phone2 && ` - ${settings.phone2}`}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Center: System / Custom Logo - Displayed prominently on EVERY page */}
                <div className="w-[24%] flex justify-center items-center flex-shrink-0">
                  <div className="relative h-20 w-20 flex items-center justify-center">
                    {settings.logoUrl && (
                      <img
                        src={settings.logoUrl}
                        alt="Logo"
                        className="h-20 w-20 object-contain rounded-[1rem] border-2 border-black p-1 bg-white shadow-sm absolute inset-0 z-10"
                        referrerPolicy="no-referrer"
                        crossOrigin="anonymous"
                      />
                    )}
                    <div className="h-20 w-20 rounded-[1rem] border-2 border-black flex flex-col items-center justify-center bg-black text-white font-black shadow-sm p-1 text-center select-none z-0">
                      <span className="text-[12px] leading-tight font-extrabold tracking-tighter">
                        {settings.name ? (settings.name.length > 10 ? settings.name.substring(0, 8) + '..' : settings.name) : t('print.logo_fallback_name')}
                      </span>
                      <span className="text-[8px] opacity-80 mt-0.5 font-bold uppercase tracking-widest">
                        {t('print.logo_fallback_sub')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Left Side: Document Metadata & Issue details */}
                <div className="text-right space-y-1 w-[38%] max-w-[38%]">
                  <div className="bg-black text-white px-3 py-1 font-black text-xs inline-block shadow-sm rounded-lg" style={{ letterSpacing: 'normal' }}>
                    {isAr ? (finalCustomerName || title) : (finalCustomerName || title).toUpperCase()}
                  </div>

                  <div className="text-[9px] font-bold text-gray-700 space-y-0.5" style={{ lineHeight: '1.3', letterSpacing: 'normal' }}>
                    <div>
                      <span className="text-black font-black">{t('print.issue_date')}</span>
                      <span className="font-mono ml-1 mr-1">{dateLabel}</span>
                    </div>
                    <div>
                      <span className="text-black font-black">{t('print.doc_no')}</span>
                      <span className="font-mono ml-1 mr-1">#{displayDocNumber}</span>
                    </div>
                    {settings.managerName && (
                      <div>
                        <span className="text-black font-black">{t('print.manager')}</span>
                        <span>{settings.managerName}</span>
                      </div>
                    )}
                    {settings.accountantName && (
                      <div>
                        <span className="text-black font-black">{t('print.accountant')}</span>
                        <span>{settings.accountantName}</span>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </div>

            {/* Top Details & Summary Section */}
            <>
              {/* Subject Section */}
              <div className="mb-1.5 text-center relative z-10 flex-shrink-0">
                <h2 className="text-sm sm:text-base font-black text-black" style={{ letterSpacing: '0px', fontFamily: 'Cairo, system-ui, sans-serif' }}>
                  {t('print.subject_prefix')}{title}
                  {pagesInfo.length > 1 && pageIndex > 0 && ` (${t('print.continued')} ${pageIndex + 1})`}
                </h2>
              </div>

              {/* Statement Date Range & Remaining Balance Bar */}
              <div className="mb-2 px-3 py-1 bg-gray-50 border border-gray-400 rounded-xl flex justify-between items-center text-[10px] font-bold relative z-10 flex-shrink-0" style={{ letterSpacing: '0px', fontFamily: 'Cairo, system-ui, sans-serif' }}>
                <div className="flex items-center gap-1.5">
                  <span className="text-black font-black">{t('print.statement_date')}</span>
                  <div className="bg-white px-2 py-0.5 rounded border border-gray-400 inline-flex items-center justify-center gap-1 text-[10px] text-gray-900 font-bold" dir="rtl">
                    {!minDate && !maxDate ? (
                      <span>-</span>
                    ) : minDate === maxDate ? (
                      <span className="font-mono" dir="ltr">{minDate}</span>
                    ) : (
                      <>
                        <span>{t('print.from')}</span>
                        <span className="font-mono px-0.5" dir="ltr">{minDate}</span>
                        <span>{t('print.to')}</span>
                        <span className="font-mono px-0.5" dir="ltr">{maxDate}</span>
                      </>
                    )}
                  </div>
                </div>
                {remainingBalance && remainingBalance.amount && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-black font-black">{t('print.remaining_balance')}</span>
                    <div className="bg-white px-2 py-0.5 rounded border border-gray-400 inline-flex items-center justify-center gap-1 text-xs font-black text-black">
                      <span className="font-mono" dir="ltr">{remainingBalance.amount}</span>
                      {remainingBalance.currency && (
                        <span className="font-sans text-[10px] text-gray-800 font-bold" dir="rtl">{remainingBalance.currency}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Salutation (Only on first page) */}
              {pageInfo.showSalutation && (
                <div className="mb-2 relative z-10 flex-shrink-0 text-center">
                  <p className="mt-0.5 text-[10.5px] leading-relaxed text-gray-700 font-medium" style={{ letterSpacing: '0px', fontFamily: 'Cairo, system-ui, sans-serif' }}>
                    {t('print.salutation')}
                  </p>
                </div>
              )}
            </>

            {/* Content Table Container - Dynamic natural height with compact standard rows and expanding multi-line cells */}
            <div className="mb-2 relative z-10 w-full flex-shrink-0">
              <table
                className="w-full border-collapse text-[10.5px] h-auto"
                style={{ border: '1.5px solid #000000', tableLayout: 'fixed' }}
              >
                <thead>
                  <tr style={{ backgroundColor: '#111827', color: '#ffffff' }}>
                    {columns.map((col, i) => (
                      <th
                        key={i}
                        className="py-1.5 px-1 text-center font-black border border-black align-middle text-[10.5px]"
                        style={{
                          border: '1.5px solid #000000',
                          width: columnWidths[i],
                          letterSpacing: '0px',
                          whiteSpace: 'nowrap',
                          fontFamily: 'Cairo, system-ui, sans-serif',
                          textRendering: 'optimizeLegibility',
                          fontFeatureSettings: '"liga" 1, "calt" 1'
                        }}
                      >
                        <div className="w-full h-full flex items-center justify-center text-center">
                          {col.header}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageInfo.data.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="p-3 text-center text-gray-500 font-bold border border-black align-middle" style={{ border: '1px solid #000000', letterSpacing: '0px' }}>
                        {t('print.no_movements')}
                      </td>
                    </tr>
                  ) : (
                    pageInfo.data.map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-100'} style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                        {columns.map((col, j) => {
                          const isNum = col.numeric === true || isNumericColumn(col.key, col.header);
                          const isBalance = (col.key || '').toLowerCase().includes('balance') ||
                                            (col.header || '').includes('الرصيد') ||
                                            (col.header || '').toLowerCase().includes('balance');
                          const rawValue = row[col.key];
                          const cellValue = rawValue !== undefined && rawValue !== null ? String(rawValue) : '-';

                          if (isBalance && cellValue !== '-') {
                            let iqdPart = '';
                            let usdPart = '';

                            if (cellValue.includes('/')) {
                              const parts = cellValue.split('/');
                              const p1 = (parts[0] ?? '').trim();
                              const p2 = (parts[1] ?? '').trim();

                              if (p1.includes('$') || p1.toUpperCase().includes('USD')) {
                                usdPart = p1;
                                iqdPart = p2;
                              } else {
                                iqdPart = p1;
                                usdPart = p2;
                              }
                            } else if (cellValue.includes('\n')) {
                              const parts = cellValue.split('\n');
                              const p1 = (parts[0] ?? '').trim();
                              const p2 = parts[1]?.trim() || '';

                              if (p1.includes('$') || p1.toUpperCase().includes('USD')) {
                                usdPart = p1;
                                iqdPart = p2;
                              } else {
                                iqdPart = p1;
                                usdPart = p2;
                              }
                            } else if (cellValue.toUpperCase().includes('IQD') || cellValue.includes('د.ع')) {
                              iqdPart = cellValue.trim();
                            } else if (cellValue.includes('$') || cellValue.toUpperCase().includes('USD')) {
                              usdPart = cellValue.trim();
                            } else {
                              iqdPart = cellValue.trim();
                            }

                            return (
                              <td
                                key={j}
                                className="py-1 px-1 text-center font-medium border border-black align-middle text-[10px]"
                                style={{
                                  border: '1px solid #000000',
                                  width: columnWidths[j],
                                  letterSpacing: '0px',
                                  lineHeight: '1.25',
                                  verticalAlign: 'middle',
                                  textAlign: 'center'
                                }}
                              >
                                <div className="w-full h-full flex flex-col items-center justify-center gap-0.5 leading-tight select-text text-center" dir="ltr">
                                  {iqdPart && (
                                    <span className="font-bold text-gray-900 whitespace-nowrap text-[10px] font-mono tracking-tight text-center">
                                      {iqdPart}
                                    </span>
                                  )}
                                  {usdPart && (
                                    <span className="font-semibold text-gray-700 whitespace-nowrap text-[9.5px] font-mono tracking-tight text-center">
                                      {usdPart}
                                    </span>
                                  )}
                                  {!iqdPart && !usdPart && (
                                    <span className="font-mono text-[10px] text-gray-900 font-bold text-center">{cellValue}</span>
                                  )}
                                </div>
                              </td>
                            );
                          }

                          const cellStyle: CSSProperties = {
                            border: '1px solid #000000',
                            width: columnWidths[j],
                            letterSpacing: '0px',
                            wordBreak: 'normal',
                            overflowWrap: 'break-word',
                            whiteSpace: 'pre-line',
                            direction: isNum ? 'ltr' : (isAr ? 'rtl' : 'ltr'),
                            unicodeBidi: isNum ? 'normal' : 'plaintext',
                            lineHeight: '1.3',
                            fontFeatureSettings: '"liga" 1, "calt" 1',
                            textRendering: 'optimizeLegibility',
                            verticalAlign: 'middle',
                            textAlign: 'center'
                          };

                          return (
                            <td
                              key={j}
                              className="py-1.5 px-1 text-center font-medium border border-black align-middle text-[10.5px]"
                              style={cellStyle}
                            >
                              <div className="w-full h-full flex items-center justify-center text-center">
                                <span
                                  dir={isNum ? 'ltr' : 'auto'}
                                  className="inline-block max-w-full select-text text-center"
                                  style={{
                                    direction: isNum ? 'ltr' : (isAr ? 'rtl' : 'ltr'),
                                    unicodeBidi: 'plaintext',
                                    letterSpacing: '0px',
                                    fontFeatureSettings: '"liga" 1, "calt" 1',
                                    textAlign: 'center'
                                  }}
                                >
                                  {cellValue}
                                </span>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Bottom Section (summary box) - [C36] the structured settlement box (:1408-1545), else the plain label/value grid (:1546-1561) */}
            {pageInfo.showSummary && (
              p.settlement ? (
                <SettlementBox s={p.settlement} t={t} isAr={isAr} wordsLabel={p.wordsLabel ?? null} hideCommission={p.hideProfit === true} />
              ) : summary && summary.length > 0 ? (
                <div className="mt-auto relative z-10 pt-1 pb-0 mb-3 flex-shrink-0" style={{ pageBreakInside: 'avoid', breakInside: 'avoid', marginBottom: '4mm' }}>
                  <div className="border-2 rounded-xl overflow-hidden shadow-sm bg-white" style={{ borderColor: "#000000" }}>
                    <div className="bg-black text-white text-center py-1 text-[11px] font-black uppercase tracking-wide">
                      {t('print.summary_title')}
                    </div>
                    <div className="grid grid-cols-2 divide-x divide-black border-t border-black bg-gray-50/50" style={{ direction: isAr ? "rtl" : "ltr" }}>
                      {summary.map((item, idx) => (
                        <div key={idx} className="p-2 flex justify-between items-center bg-white" style={{ borderRight: isAr && idx % 2 === 1 ? "1.5px solid #000000" : "none", borderLeft: !isAr && idx % 2 === 1 ? "1.5px solid #000000" : "none" }}>
                          <span className="font-black text-gray-800 text-[10px]">{item.label}:</span>
                          <span className="font-mono font-black text-xs text-black" dir="ltr">{String(item.value)}</span>
                        </div>
                      ))}
                    </div>
                    {/* [C35] Tafqeet in Words (:1538-1543) */}
                    {p.wordsLabel && (
                      <div className="bg-white py-1 px-3 text-center border-t border-gray-200 text-[10px] font-bold text-gray-900" style={{ letterSpacing: "normal" }} data-testid="statement-words">
                        {p.wordsLabel}
                      </div>
                    )}
                  </div>
                </div>
              ) : null
            )}

            {/* Footer at the absolute A4 limit */}
            <div className="absolute bottom-3 left-12 right-12 border-t-2 pt-1.5 flex justify-between items-center text-[9.5px] font-bold" style={{ borderTopColor: '#000000', color: '#111827', fontFamily: 'Cairo, sans-serif', letterSpacing: 'normal' }}>
              <p className="text-black">{settings.name} - {t('print.footer_system')}</p>
              <div className="flex items-center gap-1 text-black font-semibold">
                <span>{t('print.page')}</span>
                <span className="font-mono font-bold">{pageIndex + 1}</span>
                <span>{t('print.page_of')}</span>
                <span className="font-mono font-bold">{pagesInfo.length}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Two stacked cells (IQD over USD) of the old structured summary (:1425-1432). */
function PairCells({ v, iqdCls, usdCls }: { v: SettlementPair; iqdCls: string; usdCls: string }) {
  return (
    <div className="flex flex-col items-stretch gap-1 min-w-[130px] max-w-[160px]">
      <div className={`px-2 py-0.5 text-center rounded border font-bold text-[10.5px] bg-white leading-tight flex items-center justify-center min-h-[22px] overflow-hidden ${iqdCls}`}>
        <span dir="ltr" className="inline-block whitespace-nowrap text-center font-mono font-bold">{v.iqd}</span>
      </div>
      <div className={`px-2 py-0.5 text-center rounded border font-bold text-[10.5px] bg-white leading-tight flex items-center justify-center min-h-[22px] overflow-hidden ${usdCls}`}>
        <span dir="ltr" className="inline-block whitespace-nowrap text-center font-mono font-bold">{v.usd}</span>
      </div>
    </div>
  );
}

const DASH: SettlementPair = { iqd: '-', usd: '-' };

/** [C36] The old structured summary box, markup and classes verbatim (ProfessionalPrint.tsx:1409-1545). */
function SettlementBox({ s, t, isAr, wordsLabel, hideCommission }: { s: StatementSettlement; t: (k: string) => string; isAr: boolean; wordsLabel: string | null; hideCommission: boolean }) {
  const afterLabel = s.side === 'lakum' ? t('print.settle.after_lakum') : t('print.settle.after_alaykum');
  const badge = s.side === 'lakum' ? t('print.settle.badge_lakum') : s.side === 'alaykum' ? t('print.settle.badge_alaykum') : t('print.settle.badge_zero');
  return (
    <div className="mt-auto relative z-10 pt-1 pb-0 mb-3 flex-shrink-0" style={{ pageBreakInside: 'avoid', breakInside: 'avoid', marginBottom: '4mm' }} data-testid="statement-settlement" data-side={s.side}>
      <div className="border-2 rounded-xl overflow-hidden shadow-sm bg-white" style={{ borderColor: '#000000' }}>
        {/* Banner Header */}
        <div className="bg-black text-white text-center py-1.5 text-[11px] font-black uppercase tracking-wide" style={{ letterSpacing: 'normal' }}>
          {t('print.settle.title')}
        </div>

        {/* 2-Column Grid */}
        <div className="grid grid-cols-2 divide-x divide-black" style={{ direction: isAr ? 'rtl' : 'ltr' }}>
          {/* Column 1 (Right in RTL): Financial Activity Totals */}
          <div className="p-2.5 space-y-2 bg-white flex flex-col justify-between">
            <div className="flex justify-between items-center pb-1.5 border-b border-gray-100 gap-2">
              <span className="font-bold text-[#c2410c] text-[11px] whitespace-nowrap" style={{ letterSpacing: 'normal' }}>{t('print.settle.total_out')}:</span>
              <PairCells v={s.totalOut} iqdCls="border-gray-300 text-gray-900" usdCls="border-rose-200 text-rose-600" />
            </div>
            <div className="flex justify-between items-center pb-1.5 border-b border-gray-100 gap-2">
              <span className="font-bold text-[#0f766e] text-[11px] whitespace-nowrap" style={{ letterSpacing: 'normal' }}>{t('print.settle.total_in')}:</span>
              <PairCells v={s.totalIn} iqdCls="border-gray-300 text-gray-900" usdCls="border-emerald-200 text-emerald-600" />
            </div>
            {!hideCommission && (
              <div className="flex justify-between items-center pt-0.5 gap-2">
                <span className="font-bold text-gray-900 text-[11px] whitespace-nowrap" style={{ letterSpacing: 'normal' }}>{t('print.settle.commission')}:</span>
                <PairCells v={s.commission ?? DASH} iqdCls="border-gray-300 text-gray-900" usdCls="border-gray-300 text-gray-600" />
              </div>
            )}
          </div>

          {/* Column 2 (Left in RTL): Balances & Movements */}
          <div className="p-2.5 space-y-2 bg-white flex flex-col justify-between" style={{ borderRight: isAr ? '2px solid #000000' : 'none', borderLeft: !isAr ? '2px solid #000000' : 'none' }}>
            <div className="flex justify-between items-center pb-1.5 border-b border-gray-100 gap-2">
              <span className="font-bold text-gray-900 text-[11px] whitespace-nowrap" style={{ letterSpacing: 'normal' }}>{t('print.settle.before')}:</span>
              <PairCells v={s.before} iqdCls="border-gray-300 text-gray-900" usdCls="border-gray-300 text-gray-600" />
            </div>
            <div className="flex justify-between items-center pb-1.5 border-b border-gray-100 gap-2">
              <span className="font-bold text-[#1d4ed8] text-[11px] whitespace-nowrap" style={{ letterSpacing: 'normal' }}>{t('print.settle.last')}:</span>
              <PairCells v={s.lastMovement} iqdCls="border-sky-300 text-sky-700" usdCls="border-sky-300 text-sky-700" />
            </div>
            <div className="flex justify-between items-center pt-0.5 gap-2">
              <span className="font-bold text-[#c2410c] text-[11px] whitespace-nowrap" style={{ letterSpacing: 'normal' }}>{afterLabel}:</span>
              <PairCells v={s.after} iqdCls="border-amber-300 text-amber-800" usdCls="border-amber-300 text-amber-800" />
            </div>
          </div>
        </div>

        {/* Bottom Bar: Net Final Balance */}
        <div className="bg-[#0f172a] text-white px-3.5 py-1.5 flex justify-between items-center border-t-2 border-black gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-black text-xs text-white whitespace-nowrap" style={{ letterSpacing: 'normal' }}>{t('print.settle.net')}</span>
            <span className="px-2.5 py-0.5 rounded text-[10px] font-bold bg-white text-black whitespace-nowrap border border-gray-300 shadow-2xs" data-testid="statement-badge">{badge}</span>
          </div>
          <div className="flex items-center gap-2 min-w-[220px]">
            <div className="flex-1 text-center bg-white text-black font-black text-xs px-2.5 py-0.5 rounded border border-gray-400 leading-tight flex items-center justify-center min-h-[24px] overflow-hidden">
              <span dir="ltr" className="inline-block whitespace-nowrap text-center font-mono font-black">{s.after.iqd}</span>
            </div>
            <div className="flex-1 text-center bg-white text-black font-black text-xs px-2.5 py-0.5 rounded border border-gray-400 leading-tight flex items-center justify-center min-h-[24px] overflow-hidden">
              <span dir="ltr" className="inline-block whitespace-nowrap text-center font-mono font-black">{s.after.usd}</span>
            </div>
          </div>
        </div>

        {/* Tafqeet in Words (:1538-1543) */}
        {wordsLabel && (
          <div className="bg-white py-1 px-3 text-center border-t border-gray-200 text-[10px] font-bold text-gray-900" style={{ letterSpacing: 'normal' }} data-testid="statement-words">
            {wordsLabel}
          </div>
        )}
      </div>
    </div>
  );
}
